import { AutocompleteArrayInput, ReferenceArrayInput, TextInput, required } from 'react-admin';

import { MAX_NUMBER_OF_ENTITIES, NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { genericfilterToQuery } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { Grid, Typography } from '@mui/material';

import { CustomRichText } from '@app/components/CustomRichText';
import { LabelNotClickableBoolInput } from '@app/components/LabelNotClickableBoolInput';
import { ImageSelector } from '@app/components/common/ImageSelector';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

interface SeriesFormProps {
  title: string;
  seriesThumbnail: File | null | undefined;
  handleChangeSeriesThumbnail(logo: File): void;
}

export const SeriesForm = ({ seriesThumbnail, title, handleChangeSeriesThumbnail }: SeriesFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();

  return (
    <div>
      <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
        {title}
      </Typography>
      <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

      <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
        <Grid item xs={12}>
          <ImageSelector
            getImageId={(record: GenericObject) => record?.logo_id as string}
            getImagePath={(record: GenericObject) => (record?.logo as GenericObject)?.path as string}
            image={seriesThumbnail}
            handleChangeImage={handleChangeSeriesThumbnail}
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            variant="outlined"
            label="Name"
            helperText={false}
            validate={[required()]}
            source="name"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            variant="outlined"
            label="Short Description"
            multiline
            helperText={false}
            rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
            validate={[required()]}
            source="short_description"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            variant="outlined"
            helperText={false}
            Component={CustomRichText}
            language={language}
            label="About"
            source="about"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12}>
          <MultiLingualField
            Component={TextInput}
            language={language}
            variant="outlined"
            label="Schedule"
            multiline
            helperText={false}
            rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
            source="schedule"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <ReferenceArrayInput source="focused_topic_ids" reference="taxonomies" perPage={MAX_NUMBER_OF_ENTITIES}>
            <AutocompleteArrayInput
              variant="outlined"
              helperText={'Maximum 3 Topics'}
              autoHighlight
              label="Focused Topics"
              fullWidth
              optionText={(record) => record?.category_name[DEFAULT_LANGUAGE]}
            />
          </ReferenceArrayInput>
        </Grid>
        <Grid item xs={12}>
          <ReferenceArrayInput source="regular_expert_ids" reference="experts" perPage={MAX_NUMBER_OF_ENTITIES}>
            <AutocompleteArrayInput
              variant="outlined"
              helperText={false}
              autoHighlight
              label="Regular Experts"
              filterToQuery={genericfilterToQuery}
              fullWidth
              optionText={(record) => `${record.first_name[DEFAULT_LANGUAGE]} ${record.last_name[DEFAULT_LANGUAGE]}`}
            />
          </ReferenceArrayInput>
        </Grid>
        <Grid item xs={12}>
          <LabelNotClickableBoolInput
            helperText={false}
            label="Published"
            source="status"
            defaultValue={'INACTIVE'}
            parse={(value: boolean) => {
              if (value === true) {
                return 'ACTIVE';
              } else {
                return 'INACTIVE';
              }
            }}
            format={(value: string) => {
              if (value === 'ACTIVE') {
                return true;
              } else {
                return false;
              }
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};
